import React, { useState, useEffect, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { AlertCircle, Lock, Calendar, Eye } from 'lucide-react'
import { motion } from 'framer-motion'
import { PresentationViewer } from '@/components/presentation/PresentationViewer'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useToast } from '@/hooks/use-toast'
import type { PresentationData, SlideData } from '@/types/presentation'

interface SharedPresentationResponse {
  success: boolean
  presentation: {
    id: string
    title: string
    description: string | null
    metadata: Record<string, unknown>
    content?: {
      metadata: Record<string, unknown>
      slides: unknown[]
    } | null
  }
  share: {
    accessLevel: 'public' | 'private' | 'restricted'
    hasPassword: boolean
    expiresAt: string | null
  }
  error?: string
  requiresPassword?: boolean
}

export function SharedPresentation() {
  const { token } = useParams<{ token: string }>()
  const navigate = useNavigate()
  const { toast } = useToast()

  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [requiresPassword, setRequiresPassword] = useState(false)
  const [password, setPassword] = useState('')
  const [presentation, setPresentation] = useState<PresentationData | null>(null)
  const [shareInfo, setShareInfo] = useState<SharedPresentationResponse['share'] | null>(null)
  const [isExpired, setIsExpired] = useState(false)

  const loadPresentation = useCallback(async (shareToken: string, sharePassword?: string) => {
    setIsLoading(true)
    setError(null)

    try {
      const queryParams = new URLSearchParams()
      if (sharePassword) {
        queryParams.append('password', sharePassword)
      }

      const response = await fetch(`/api/shared/${shareToken}${queryParams.toString() ? `?${queryParams}` : ''}`)

      if (response.status === 404) {
        setError('Share link not found. It may have been deleted.')
        setIsLoading(false)
        return
      }

      if (response.status === 410) {
        setError('This share link has expired.')
        setIsExpired(true)
        setIsLoading(false)
        return
      }

      if (response.status === 401) {
        const data = await response.json()
        if (data.requiresPassword) {
          setRequiresPassword(true)
          setIsLoading(false)
          return
        }
        setError('Invalid password.')
        setIsLoading(false)
        return
      }

      if (!response.ok) {
        throw new Error('Failed to load presentation')
      }

      const data: SharedPresentationResponse = await response.json()

      if (!data.success || !data.presentation) {
        throw new Error(data.error || 'Failed to load presentation')
      }

      // Build presentation data from response
      const presentationData: PresentationData = {
        slides: (data.presentation.content?.slides || []) as SlideData[],
        metadata: {
          title: data.presentation.title,
          description: data.presentation.description || undefined,
          ...(data.presentation.metadata as Record<string, unknown>),
        },
      }

      setPresentation(presentationData)
      setShareInfo(data.share)
      setRequiresPassword(false)
      setIsLoading(false)
    } catch (err) {
      console.error('Failed to load shared presentation:', err)
      setError('Failed to load presentation. Please try again later.')
      setIsLoading(false)
    }
  }, [])

  // Load presentation data
  useEffect(() => {
    if (!token) {
      setError('Invalid share link')
      setIsLoading(false)
      return
    }

    loadPresentation(token)
  }, [token, loadPresentation])

  // Hide Chat widget when shared presentation is mounted
  useEffect(() => {
    // Hide Chat widget
    const chatWidgetButton = document.querySelector('button[aria-label="Toggle chat"]')
    if (chatWidgetButton) {
      ;(chatWidgetButton as HTMLElement).style.display = 'none'
    }

    // Also hide by class if the aria-label changes
    const chatWidgets = document.querySelectorAll('[style*="background: linear-gradient(135deg, rgb(59, 130, 246)"][class*="fixed"]')
    chatWidgets.forEach(widget => {
      ;(widget as HTMLElement).style.display = 'none'
    })

    // Cleanup: restore visibility when unmounting
    return () => {
      if (chatWidgetButton) {
        ;(chatWidgetButton as HTMLElement).style.display = ''
      }
      chatWidgets.forEach(widget => {
        ;(widget as HTMLElement).style.display = ''
      })
    }
  }, [])

  const handlePasswordSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!token) return

    if (!password.trim()) {
      toast({
        title: 'Password required',
        description: 'Please enter the password to access this presentation.',
        variant: 'destructive',
      })
      return
    }

    loadPresentation(token, password)
  }

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-950">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center"
        >
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Loading presentation...</p>
        </motion.div>
      </div>
    )
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-950 p-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-md w-full"
        >
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl p-8 text-center">
            <div className="w-16 h-16 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="w-8 h-8 text-red-600 dark:text-red-400" />
            </div>

            <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
              {isExpired ? 'Link Expired' : 'Unable to Load Presentation'}
            </h1>

            <p className="text-gray-600 dark:text-gray-400 mb-6">
              {error}
            </p>

            <div className="space-y-3">
              <Button
                onClick={() => navigate('/')}
                className="w-full"
              >
                Go to Homepage
              </Button>

              {requiresPassword && (
                <Button
                  variant="outline"
                  onClick={() => setError(null)}
                  className="w-full"
                >
                  Try Again
                </Button>
              )}
            </div>

            {/* securesight.ai branding */}
            <div className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Shared via{' '}
                <span className="font-semibold text-primary-600 dark:text-primary-400">
                  securesight.ai
                </span>
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    )
  }

  // Password prompt
  if (requiresPassword && !presentation) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-950 p-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-md w-full"
        >
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl p-8">
            <div className="w-16 h-16 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
              <Lock className="w-8 h-8 text-blue-600 dark:text-blue-400" />
            </div>

            <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2 text-center">
              Password Required
            </h1>

            <p className="text-gray-600 dark:text-gray-400 mb-6 text-center">
              This presentation is password protected. Please enter the password to view it.
            </p>

            <form onSubmit={handlePasswordSubmit} className="space-y-4">
              <div>
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter password"
                  className="mt-1"
                  autoFocus
                />
              </div>

              <Button type="submit" className="w-full">
                Unlock Presentation
              </Button>
            </form>

            {/* securesight.ai branding */}
            <div className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-700 text-center">
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Shared via{' '}
                <span className="font-semibold text-primary-600 dark:text-primary-400">
                  securesight.ai
                </span>
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    )
  }

  // Presentation loaded - show viewer
  if (presentation) {
    return (
      <div className="relative">
        {/* Info bar */}
        {shareInfo && (
          <div className="fixed top-0 left-0 right-0 z-50 bg-gradient-to-r from-blue-600 to-purple-600 text-white px-4 py-2 text-sm flex items-center justify-between">
            <div className="flex items-center gap-4">
              <span className="font-semibold">securesight.ai Shared Presentation</span>

              {shareInfo.accessLevel === 'public' && (
                <div className="flex items-center gap-1">
                  <Eye className="w-4 h-4" />
                  <span>Public</span>
                </div>
              )}

              {shareInfo.accessLevel === 'private' && (
                <div className="flex items-center gap-1">
                  <Lock className="w-4 h-4" />
                  <span>Password Protected</span>
                </div>
              )}

              {shareInfo.expiresAt && (
                <div className="flex items-center gap-1">
                  <Calendar className="w-4 h-4" />
                  <span>Expires: {new Date(shareInfo.expiresAt).toLocaleDateString()}</span>
                </div>
              )}
            </div>

            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/')}
              className="text-white hover:bg-white/20"
            >
              Go to securesight.ai
            </Button>
          </div>
        )}

        {/* Presentation Viewer with top padding for info bar */}
        <div className="pt-12">
          <PresentationViewer presentation={presentation} />
        </div>
      </div>
    )
  }

  return null
}
